import { useCallback } from "react";
import { useObserver } from "mobx-react-lite";
import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { activeOfficeStore } from "@Front/Stores/ActiveOfficeStore";
import { useRolePermissions } from "@Front/Stores/RolePermissionsStore";
import { checkPermissionInMatrix } from "@Front/Stores/hooks/rolePermissionsMatrixCheck";
import {
	resolveNotaryMenuOfficeIdentity,
	type NotaryMenuOfficeIdentity,
} from "@Front/Utils/resolveNotaryMenuOfficeIdentity";
import { GUEST_FOLDERS_UID, isGuestFoldersAffiliation } from "@Front/Utils/preferredRealOfficeUidHelpers";
import { getActiveAffiliatedRealOfficeUidsFromStore } from "@Front/Stores/helpers/activeOfficeAffiliatedRealOfficeUidsHelpers";
import { resolveOfficeScopedEntityForGuestFolderWorkspace } from "@Front/Utils/notaryGuestFolderWorkspaceHelpers";
import type {
	OfficeScopedPermissionAction,
	OfficeScopedPermissionCanFn,
	OfficeScopedPermissionResource,
} from "@Front/Utils/permissionCanTypes";

function isRealMenuOfficeUid(menuOfficeUid: string): boolean {
	return (
		Boolean(menuOfficeUid) &&
		menuOfficeUid !== GUEST_FOLDERS_UID &&
		!isGuestFoldersAffiliation({ office_uid: menuOfficeUid, uid: menuOfficeUid })
	);
}

function matrixContextForOfficeSelection(
	permissionContext: PermissionContextDto,
	identity: NotaryMenuOfficeIdentity,
): { ctxForMatrix: PermissionContextDto; scopedEntity: string | null } {
	const menuOfficeUid = identity.menuOfficeUid;
	const hasRealMenuOffice = isRealMenuOfficeUid(menuOfficeUid);
	const ctxForMatrix: PermissionContextDto =
		hasRealMenuOffice ? { ...permissionContext, activeOfficeUid: menuOfficeUid } : permissionContext;
	return { ctxForMatrix, scopedEntity: hasRealMenuOffice ? menuOfficeUid : null };
}

/**
 * `can` matrice résolue sur l'office réellement sélectionné dans l'UI.
 * Peut être utilisé par le menu office et par des flux métier hors header (ex: création dossier).
 */
export function useOfficeScopedPermissionCan(): OfficeScopedPermissionCanFn {
	const { matrix, permissionContext } = useRolePermissions();
	const { activeOffice, availableOffices, lastRealQuiet } = useObserver(() => ({
		activeOffice: activeOfficeStore.activeOffice,
		availableOffices: activeOfficeStore.availableOffices,
		lastRealQuiet: activeOfficeStore.peekLastRealOfficeUidBeforeGuest(),
	}));

	return useCallback(
		(resource: OfficeScopedPermissionResource, action: OfficeScopedPermissionAction, scopedEntityArg?: string | null) => {
			if (!permissionContext) {
				return false;
			}
			const identity = resolveNotaryMenuOfficeIdentity({
				activeOffice,
				availableOffices,
				lastRealOfficeUidBeforeGuest: lastRealQuiet,
				permissionContext,
			});
			const { ctxForMatrix, scopedEntity } = matrixContextForOfficeSelection(permissionContext, identity);
			const effectiveScopedEntity = resolveOfficeScopedEntityForGuestFolderWorkspace({
				permissionContext,
				candidateScopedEntity:
					typeof scopedEntityArg === "string" && scopedEntityArg.length > 0 ? scopedEntityArg : scopedEntity,
				menuOfficeUid: scopedEntity,
				affiliatedRealOfficeUids: getActiveAffiliatedRealOfficeUidsFromStore(),
			});
			return checkPermissionInMatrix({
				resource,
				action,
				scopedEntity: effectiveScopedEntity,
				matrix,
				permissionContext: ctxForMatrix,
			});
		},
		[matrix, permissionContext, activeOffice, availableOffices, lastRealQuiet],
	);
}
